import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { FormattedMessage } from 'react-intl';

import { modalsSlice } from 'features/modals/modalsSlice';
import { selectMatchStatusId } from 'features/match/matchSlice';

import Button from 'styled/components/Button';

import { StatusButtonProps } from './StatusButton';
import { MatchStatus } from '../../enums/matchStatus';

const StatusButton: React.FC<StatusButtonProps> = ({ matchStatusId, penaltiesShootoutStarted, all }) => {
  const dispatch = useDispatch();

  const currentMatchStatusId = useSelector(selectMatchStatusId);

  const BeforeMatchButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.NOT_STARTED}
      onClick={() => {
        dispatch(modalsSlice.actions.setMatchNotStartedModal(true));
      }}
    >
      <FormattedMessage id="states.beforeMatch" />
    </Button>
  );

  const StartFirstHalfButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.FIRST_HALF}
      onClick={() => {
        dispatch(modalsSlice.actions.setStartMatchModal(true));
      }}
    >
      <FormattedMessage id={all ? 'states.firstHalf' : 'states.startFirstHalf'} />
    </Button>
  );

  const StartHalfTimeButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.HALF_TIME}
      onClick={() => {
        dispatch(modalsSlice.actions.setPauseMatchModal(true));
      }}
    >
      <FormattedMessage id="states.pause" />
    </Button>
  );

  const StartSecondHalfButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.SECOND_HALF}
      onClick={() => {
        dispatch(modalsSlice.actions.setSecondHalfModal(true));
      }}
    >
      <FormattedMessage id={all ? 'states.secondHalf' : 'states.startSecondHalf'} />
    </Button>
  );

  const EndMatchButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.PLAYED}
      onClick={() => {
        dispatch(modalsSlice.actions.setMatchEndedModal(true));
      }}
    >
      <FormattedMessage id={all ? 'states.afterMatch' : 'states.matchEnded'} />
    </Button>
  );

  const StartFirstHalfExtraTimeButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.FIRST_HALF_EXTRA_TIME}
      onClick={() => {
        dispatch(modalsSlice.actions.setFirstHalfExtraTimeModal(true));
      }}
    >
      <FormattedMessage id={all ? 'states.firstHalfExtraTime' : 'states.startFirstHalfExtraTime'} />
    </Button>
  );

  const HalfTimeExtraTimeButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.HALF_TIME_EXTRA_TIME}
      onClick={() => {
        dispatch(modalsSlice.actions.setHalfTimeExtraTimeModal(true));
      }}
    >
      <FormattedMessage id="states.halfTimeExtraTime" />
    </Button>
  );

  const StartSecondHalfExtraTimeButton = () => (
    <Button
      disabled={currentMatchStatusId === MatchStatus.SECOND_HALF_EXTRA_TIME}
      onClick={() => {
        dispatch(modalsSlice.actions.setSecondHalfExtraTimeModal(true));
      }}
    >
      <FormattedMessage id={all ? 'states.secondHalfExtraTime' : 'states.startSecondHalfExtraTime'} />
    </Button>
  );

  const PenaltiesButton = () => {
    return (
      <Button
        disabled={currentMatchStatusId === MatchStatus.PENALTIES && penaltiesShootoutStarted}
        onClick={() => {
          dispatch(modalsSlice.actions.setPenaltiesModal(true));
        }}
      >
        <FormattedMessage id={all ? 'states.penalties' : 'states.startPenaltyShootout'} />
      </Button>
    );
  };

  const MatchEndedButton = () => (
    <Button disabled>
      <FormattedMessage id="states.matchEnded" />
    </Button>
  );

  if (all) {
    return (
      <>
        <BeforeMatchButton />
        <StartFirstHalfButton />
        <StartHalfTimeButton />
        <StartSecondHalfButton />
        <StartFirstHalfExtraTimeButton />
        <HalfTimeExtraTimeButton />
        <StartSecondHalfExtraTimeButton />
        <PenaltiesButton />
        <EndMatchButton />
      </>
    );
  }

  switch (matchStatusId) {
    case MatchStatus.NOT_STARTED:
      return <StartFirstHalfButton />;

    case MatchStatus.PLAYED:
      return <MatchEndedButton />;

    case MatchStatus.FIRST_HALF:
      return <StartHalfTimeButton />;

    case MatchStatus.HALF_TIME:
      return <StartSecondHalfButton />;

    case MatchStatus.SECOND_HALF:
    case MatchStatus.SECOND_HALF_EXTRA_TIME:
      return <EndMatchButton />;

    case MatchStatus.PENALTIES:
      if (penaltiesShootoutStarted) {
        return <EndMatchButton />;
      } else {
        return <PenaltiesButton />;
      }

    case MatchStatus.PAUSE_BEFORE_EXTRA_TIME:
      return <StartFirstHalfExtraTimeButton />;

    case MatchStatus.FIRST_HALF_EXTRA_TIME:
      return <HalfTimeExtraTimeButton />;

    case MatchStatus.HALF_TIME_EXTRA_TIME:
      return <StartSecondHalfExtraTimeButton />;
  }

  return <div />;
};

export default StatusButton;
